import { useState, useContext } from "react";
import { useMutation } from "@apollo/client";
import { useToast } from "./useToast";
import LoaderContext from "../context/LoaderContext";

/**
 * Custom hook for handling delete confirmation workflows
 * @param {Object} mutation - GraphQL mutation for deletion
 * @param {Function} refetch - Function to refetch data after deletion
 * @param {string} successMessage - Success message to show
 * @param {string} errorMessage - Error message to show
 * @param {Function} onSuccess - Optional callback to run on successful deletion
 * @returns {Object} - Delete confirmation state and handlers
 */
export const useDeleteConfirmation = (
  mutation,
  refetch,
  successMessage = "Item deleted successfully.",
  errorMessage = "Something went wrong.",
  onSuccess = null
) => {
  const { setIsLoading } = useContext(LoaderContext);
  const { showToast } = useToast();
  const [deleteMutation] = useMutation(mutation);
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    id: null,
  });

  const openDeleteModal = (item) => {
    setDeleteModal({ open: true, id: item.id });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ open: false, id: null });
  };

  const performDelete = async () => {
    try {
      setIsLoading(true);
      await deleteMutation({
        variables: {
          id: deleteModal.id,
        },
      });
      setIsLoading(false);
      refetch();
      showToast("success", successMessage);

      // Call the onSuccess callback if provided
      if (typeof onSuccess === "function") {
        onSuccess();
      }

      closeDeleteModal();
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      showToast("error", errorMessage);
    }
  };

  return {
    deleteModal,
    openDeleteModal,
    closeDeleteModal,
    performDelete,
  };
};
